import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Switch,
  CircularProgress
} from '@mui/material';

import {
  Configurator,
  CustomSelect,
  MDBox,
  MDTypography,
  If,
  CommentsList,
  FaIcon,
  AutoCategorizationModal,
  SqBChatWidget,
  FullPageLoader,
  HeatMap,
  UpDownVote,
  InsightsModal,
  DownloadPreviewModal,
  CleanActionsModal
} from '@components/index';
import MainLayout from '@layouts/MainLayout';
import ChartHeader from './components/ChartHeader';
import Settings from './components/Settings';
import ThemeMenu from './components/Settings/components/ThemeMenu';
import Filter from './components/Filter';
import GlobalFilter from './components/GlobalFilter';
import ReportGenerationModal from '@pages/ProjectBoard/components/ReportGenerationModal';
import brandLogo from '@assets/images/DS_Logo.png';

import { DataState } from './slice';
import { ChartConfigData } from '@api/chart';
import {
  AvailableSettings,
  ChartCreationOptions,
  VisualSettings
} from '@DStypes/chart';
import { UserDetails } from '@DStypes/User';
import { IGlobalFilters, MarkType } from '@DStypes/filter';
import { setOpenConfigurator } from '@context/MDContext';

import { sendAmplitudeData } from '@utils/amplitude';
import { getChartTheme, reOrderChartTypes } from '@utils/chart';
import linearGradient from '@theme/functions/linearGradient';
import { DATE_GROUP, numberEquivalents } from '@constants/common';
import {
  // CHANGE_CHART_OPERATION,
  CHANGE_CHART_TYPE,
  CHANGE_SPLIT_BY,
  CHANGE_X_AXIS,
  CLICKED_DOWNLOAD,
  CLICKED_DS_RECOMMENDATION,
  CLICKED_FILTER,
  CLICKED_SETTINGS,
  CLICKED_TABLE_ICON,
  CLICKED_THEME
} from '@constants/eventNames';
import { GO_BACK_MESSAGE } from '@constants/toolTips';

const getChartTypeIcon = (chartType: string) => {
  switch (chartType) {
    case 'Line':
      return 'chart-line';
    case 'Column':
      return 'chart-column';
    case 'PIE':
      return 'chart-pie';
    case 'Bar':
      return 'chart-bar';
    default:
      return '';
  }
};

const getDropDownValueDataType = (options: any, selectedValue: any) => {
  const value = options.filter((option: any) => option.value === selectedValue);

  return value[0]?.type;
};

export interface IEditChartComponentProps {
  chartType: string | undefined;
  createdAt: string | undefined;
  chartConfigData: ChartConfigData | undefined;
  isDropdownDataLoading: boolean;
  isGetChartsLoading: boolean;
  setShowDownloadPreview: React.Dispatch<React.SetStateAction<boolean>>;
  dropdownData: any;
  handleDisableChart: (
    chartType: string,
    isSelfChart: boolean,
    xAxis?: string,
    yAxis?: string
  ) => boolean;
  dropDownValues: {
    chart_type: string;
    chart_operation: string;
    x_axis: string;
    y_axis: string;
    segment_field: string;
  };
  handleDropdownChange: (
    value: string | undefined,
    field: 'chart_type' | 'chart_operation' | 'segment_field'
  ) => Promise<void>;
  currentChartData: DataState;
  chartOperationOptions: any[];
  yAxisOptions: any[];
  handleXYSelection: (
    value: string | undefined,
    field: 'x_axis' | 'y_axis'
  ) => Promise<void>;
  xAxis: any;
  xAxisOptions: any[];
  userSetDateGroup: string | null;
  chartId: string | undefined;
  segmentFields: any[];
  visualSettings: VisualSettings | null;
  infoText: any;
  heatMapRef: React.MutableRefObject<HTMLButtonElement | null>;
  cardRef: React.MutableRefObject<HTMLDivElement | null>;
  domRef: React.MutableRefObject<HTMLElement | undefined>;
  debouncedChart:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  isLoading: boolean;
  isDataLoading: boolean;
  configuratorFlag: number;
  setConfiguratorFlag: React.Dispatch<React.SetStateAction<number>>;
  onCloseConfigurator: () => void;
  isChartLoading: boolean;
  chartData: any;
  config: any;
  setVisualSettings: React.Dispatch<
    React.SetStateAction<VisualSettings | null>
  >;
  yAxis: any;
  handleDateGroupChange: (value: DATE_GROUP | '') => Promise<void>;
  setIsManuallySetStackChart: React.Dispatch<React.SetStateAction<boolean>>;
  setIsManuallySetLegends: React.Dispatch<React.SetStateAction<boolean>>;
  stackColumnName: string;
  selectedTheme: string[] | null | undefined;
  projectColorSet: any;
  updateTheme: (arg: any) => void;
  projectData: any;
  chartRefetchFunction: () => void;
  filterProps: {
    getSliderToolTip: () => string;
    renderSliderLabel: (value: number) => string;
    list: JSX.Element[];
    handleTopOrBottom: (
      value: boolean,
      field?: 'top' | 'bottom' | undefined
    ) => void;
    handleSelectAll: (value: boolean) => void;
    setSliderDrag: React.Dispatch<React.SetStateAction<boolean>>;
    preventClosing: any;
    topOrBottom: string;
    sliderValue: number;
    handleSliderChange: (value: number, topOrBottom: string) => void;
    sliderMarks: MarkType[];
    setShowFilter: React.Dispatch<React.SetStateAction<boolean>>;
    showFilter: boolean;
    xAxisField: any;
    dateSliderDisabled: boolean;
    getDateString: (index: number) => string;
    sliderRangeValue: number[];
    handleSliderRangeChange: (
      [min, max]: number[],
      isXAxisSelection?: boolean
    ) => void;
    setDateSliderDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    xAxisLabelsExcluded: string[];
    searchTextValue: string;
    setSearchTextValue: React.Dispatch<React.SetStateAction<string>>;
    autoGroupNumbers: boolean | undefined;
  };
  filterTooltipText: string;
  calenderTooltipText: string;
  filterText: string;
  commentsList: any;
  onCreateChartComment: (commentDetails: any) => void;
  onClickRemoveWaterMark: () => void;
  isAutoCategorizationOpen: boolean;
  handleToggleAutoCategorization: () => Promise<void>;
  segmentField: any;
  subscriptionFlag: boolean;
  isAutoCategorizationEnabledByPlan: boolean;
  invalidOperation: string | undefined;
  handleCloseSnackbar: () => void;
  axisFlipped: boolean;
  handleCloseAxisFlippedSnackbar: () => void;
  showDownloadPreview: boolean;
  openCleanActionsModal: boolean;
  activeDateGroup: any;
  isRemoveWaterMarkEnabledByPlan: boolean;
  userDetails: UserDetails;
  infoSnackBarOpen: boolean;
  reloadDataTable: boolean;
  setInfoSnackBarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  snackBarMessage: string;
  chartFilters: string[];
  getAutoCategorizationColumnOptions: () => any[];
  isAutoCategorizationLoading: boolean;
  setAutoCategorizationOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAutoCategorizationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCleanActionsModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleInitiateAutoCategorization: (field: string, count: number) => void;
  handleCompleteAutoCategorization: (
    columnId: string,
    selectedColumn: string
  ) => Promise<void>;
  controller: any;
  dispatch: any;
  setRefetchChart: React.Dispatch<React.SetStateAction<boolean>>;
  availableSettings: AvailableSettings;
  openChartInsightsModal: boolean;
  setOpenChartInsightsModal: React.Dispatch<React.SetStateAction<boolean>>;
  showRedFlag: boolean;
  setShowRedFlag: React.Dispatch<React.SetStateAction<boolean>>;
  setIsManuallySetDashboardFilterToggle: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

const EditChartComponent: React.FC<IEditChartComponentProps> = ({
  activeDateGroup,
  axisFlipped,
  cardRef,
  chartConfigData,
  chartData,
  chartFilters,
  chartId,
  chartOperationOptions,
  chartRefetchFunction,
  chartType,
  commentsList,
  config,
  configuratorFlag,
  controller,
  createdAt,
  currentChartData,
  debouncedChart,
  dispatch,
  domRef,
  dropDownValues,
  dropdownData,
  filterProps,
  filterTooltipText,
  filterText,
  getAutoCategorizationColumnOptions,
  handleCloseAxisFlippedSnackbar,
  handleCloseSnackbar,
  handleCompleteAutoCategorization,
  handleDateGroupChange,
  handleDisableChart,
  handleDropdownChange,
  handleInitiateAutoCategorization,
  handleToggleAutoCategorization,
  handleXYSelection,
  infoSnackBarOpen,
  infoText,
  invalidOperation,
  isAutoCategorizationEnabledByPlan,
  isAutoCategorizationLoading,
  isAutoCategorizationOpen,
  isChartLoading,
  isDropdownDataLoading,
  isGetChartsLoading,
  onCloseConfigurator,
  onClickRemoveWaterMark,
  onCreateChartComment,
  projectColorSet,
  projectData,
  reloadDataTable,
  segmentField,
  segmentFields,
  selectedTheme,
  setAutoCategorizationLoading,
  setAutoCategorizationOpen,
  setConfiguratorFlag,
  setInfoSnackBarOpen,
  setIsManuallySetLegends,
  setIsManuallySetStackChart,
  setShowDownloadPreview,
  setOpenCleanActionsModal,
  setVisualSettings,
  showDownloadPreview,
  heatMapRef,
  openCleanActionsModal,
  snackBarMessage,
  stackColumnName,
  subscriptionFlag,
  updateTheme,
  userSetDateGroup,
  visualSettings,
  xAxis,
  xAxisOptions,
  yAxis,
  yAxisOptions,
  setRefetchChart,
  availableSettings,
  openChartInsightsModal,
  setOpenChartInsightsModal,
  showRedFlag,
  setShowRedFlag,
  setIsManuallySetDashboardFilterToggle
}): React.ReactElement<any, any> | null => {
  const location: any = useLocation();
  const { openConfigurator, preventClosing } = controller;

  const typographyRef = useRef<HTMLDivElement>(null);
  const showTooltip =
    typographyRef?.current &&
    typographyRef?.current?.getBoundingClientRect().height > 60
      ? true
      : false;

  const [openSqBChatWidget, setOpenSqBChatWidget] = useState(false);
  const [openHeatMap, setOpenHeatMap] = useState<boolean>(false);
  const [cleanedColumnsRedirect, setCleanedColumnsRedirect] =
    useState<boolean>(false);
  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false);
  const chartHeaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => {
      if (location?.state?.openFilter) onOpenSlideIn(2, false);
    }, 500);
  }, []);

  useEffect(() => {
    if (openHeatMap) handleScrollToHeatMap();
  }, [openHeatMap]);

  const chartHeaderHeight = useMemo(() => {
    if (chartHeaderRef?.current) return chartHeaderRef?.current?.clientHeight;
  }, [
    chartHeaderRef?.current?.clientHeight,
    currentChartData?.chart_short_desc
  ]);

  const onOpenSlideIn = useDebouncedCallback(
    (active: number, openConfigurator: boolean) => {
      if (configuratorFlag === null || !openConfigurator) {
        setConfiguratorFlag(active);
        setOpenConfigurator(dispatch, true);
      } else {
        if (openConfigurator)
          if (configuratorFlag === active) {
            // setOpenConfigurator(dispatch, false);
            // setTimeout(() => {
            //   setConfiguratorFlag(0);
            // }, 400);
          } else {
            setOpenConfigurator(dispatch, false);
            setTimeout(() => {
              setConfiguratorFlag(active);
              setOpenConfigurator(dispatch, true);
            }, 300);
          }
      }
    },
    300
  );

  const debouncedDownload = useDebouncedCallback(() => {
    sendAmplitudeData(CLICKED_DOWNLOAD);
    setShowDownloadPreview(true);
  }, 500);

  const onClickDownloadOrCancel = () => {
    setOpenDownloadModal((value) => !value);
    if (cleanedColumnsRedirect) setCleanedColumnsRedirect(false);
  };

  const handleCleanedDataDownloadOrCancel = () => {
    setCleanedColumnsRedirect((prev) => !prev);
    onClickDownloadOrCancel();
  };

  const handleScrollToHeatMap = () => {
    if (isChartLoading || reloadDataTable) {
      heatMapRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });

      return;
    }
    const targetObject = document?.getElementById('stickyTableWrapper');

    if (targetObject)
      window.scrollTo({
        top: window.scrollY + targetObject.getBoundingClientRect()?.top,
        behavior: 'smooth'
      });
  };

  const handleTableIconClick = () => {
    heatMapRef.current?.click();
    setOpenHeatMap(true);
    handleScrollToHeatMap();
    sendAmplitudeData(CLICKED_TABLE_ICON);
  };

  const handleHeatMapArrowClick = () => {
    if (!openHeatMap) sendAmplitudeData(CLICKED_TABLE_ICON);
    if (setOpenHeatMap) setOpenHeatMap((prev) => !prev);
  };

  return (
    <MainLayout
      sectionTitle={chartType ? (chartType === 'KPI' ? 'KPI' : 'Chart') : ''}
      pageTitle=''
      showBackButton={true}
      createdAt={createdAt}
      goBackMsg={GO_BACK_MESSAGE}
      sectionTooltip={
        chartType === 'KPI'
          ? 'Customise your KPI\'s settings on this page - change KPI type, add segment, filter KPI values, download, comment & share.'
          : 'Customise your chart\'s settings on this page - change chart type, add segment, filter chart values, download, comment & share.'
      }
      goBackDestination={`/project/${chartConfigData?.chart_project}/edit`}
      padding='15px 8px 15px 8px'
      page='EditChart'
    >
      <MDBox position={'relative'}>
        {isDropdownDataLoading || isGetChartsLoading ? (
          <FullPageLoader />
        ) : (
          <Grid
            container
            spacing={1}
            direction='column'
            style={{
              position: 'relative'
            }}
            id='drawer-container'
            px='8px'
          >
            <div id='chart-header' ref={chartHeaderRef}>
              <Card sx={{ my: 1 }}>
                <ChartHeader
                  projectId={chartConfigData?.chart_project || ''}
                  setOpenCleanActionsModal={setOpenCleanActionsModal}
                  setOpenChartInsightsModal={setOpenChartInsightsModal}
                  showRedFlag={showRedFlag}
                  setShowRedFlag={setShowRedFlag}
                />
              </Card>

              {dropdownData && (
                <Card
                  sx={{
                    padding: '8px 16px'
                  }}
                >
                  <MDBox
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    pl={1}
                  >
                    <Grid
                      container
                      spacing={1}
                      direction='row'
                      alignItems='center'
                      width='100%'
                    >
                      <Grid item>
                        <Grid
                          container
                          display='flex'
                          flexDirection='row'
                          alignItems='center'
                          alignContent='center'
                        >
                          <UpDownVote
                            rating={currentChartData.rating}
                            chartDetails={{
                              chart_long_desc: currentChartData.chart_long_desc,
                              chart_short_desc:
                                currentChartData.chart_short_desc,
                              chart_title: currentChartData.chart_title
                            }}
                            chartId={chartId as string}
                            amplitudeProps={{
                              data_type_combination: `${currentChartData.config?.x_axis?.treat_as_type} & ${currentChartData.config?.y_axis?.treat_as_type}`,
                              method_used:
                                currentChartData?.method_used ||
                                ChartCreationOptions.SqB,
                              chart_config_count:
                                currentChartData?.chart_config_count
                            }}
                            isEditChart
                          />
                          <CustomSelect
                            tooltipText='Change Chart type'
                            placeholder='Chart Type'
                            color='white'
                            sx={{
                              ml: 2,
                              display: chartType === 'KPI' ? 'none' : 'span'
                            }}
                            options={
                              reOrderChartTypes(
                                dropdownData.chart_type_choices.map(
                                  (chart_type: any) => ({
                                    ...chart_type,
                                    disabled: handleDisableChart(
                                      chart_type.value,
                                      dropDownValues?.x_axis ===
                                        dropDownValues?.y_axis
                                        ? true
                                        : false
                                    ),
                                    icon: getChartTypeIcon(chart_type.value)
                                  })
                                )
                              ) || []
                            }
                            value={dropDownValues.chart_type}
                            disabledTooltipText='This chart type is not available for this kind of data'
                            handleValueChange={(e: any) => {
                              handleDropdownChange(
                                e.target.value,
                                'chart_type'
                              );
                              sendAmplitudeData(CHANGE_CHART_TYPE, {
                                'Created Using':
                                  currentChartData.created_by_auto_charts
                                    ? 'Auto'
                                    : 'Manual'
                              });
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Grid
                        item
                        container
                        flex={1}
                        justifyContent='center'
                        alignItems='center'
                        spacing={1}
                      >
                        <Grid item marginTop='8px' marginLeft='16px'>
                          <Grid
                            container
                            spacing={1}
                            direction='row'
                            justifyContent='center'
                            alignItems='center'
                            border='1px black solid'
                            padding='8px'
                            borderRadius='4px'
                          >
                            <Grid
                              item
                              style={{ paddingTop: '0px', paddingLeft: '0px' }}
                            >
                              <CustomSelect
                                tooltipText='Change Operation'
                                color='white'
                                placeholder='Operation'
                                options={chartOperationOptions || []}
                                disabledTooltipText='This option is not available for this kind of data'
                                value={dropDownValues.chart_operation}
                                handleValueChange={(e: any) => {
                                  handleDropdownChange(
                                    e.target.value,
                                    'chart_operation'
                                  );
                                }}
                              />
                            </Grid>
                            <Grid item style={{ paddingTop: '0px' }}>
                              <MDTypography
                                component='span'
                                variant='sm'
                                color='info'
                                mr={1}
                                pr='8px'
                              >
                                of
                              </MDTypography>

                              <CustomSelect
                                tooltipText={getDropDownValueDataType(
                                  dropdownData.available_fields,
                                  chartType === 'KPI'
                                    ? dropDownValues.x_axis
                                    : dropDownValues.y_axis
                                )}
                                placeholder='Select First Column Name'
                                color='white'
                                options={
                                  chartType === 'KPI'
                                    ? dropdownData?.available_fields.map(
                                        (field: any) => ({
                                          ...field,
                                          label: String(field.label),
                                          disabled: false
                                        })
                                      )
                                    : yAxisOptions || []
                                }
                                value={
                                  chartType === 'KPI'
                                    ? dropDownValues.x_axis
                                    : dropDownValues.y_axis
                                }
                                axis='Y'
                                handleValueChange={(e: any) => {
                                  handleXYSelection(
                                    e.target.value,
                                    chartType === 'KPI' ? 'x_axis' : 'y_axis'
                                  );
                                }}
                                xAxisColumnName={xAxis?.label}
                              />
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid
                          item
                          sx={{
                            display: chartType === 'KPI' ? 'none' : 'span'
                          }}
                        >
                          <MDTypography
                            component='span'
                            variant='sm'
                            color='info'
                            mr={1}
                          >
                            per
                          </MDTypography>
                          <CustomSelect
                            tooltipText={getDropDownValueDataType(
                              dropdownData.available_fields,
                              dropDownValues.x_axis
                            )}
                            placeholder='Select Second Column Name'
                            color='white'
                            options={xAxisOptions || []}
                            value={dropDownValues.x_axis}
                            axis='X'
                            handleValueChange={(e: any) => {
                              // const visitedCharts = JSON.parse(
                              //   LocalStorage.getItem(
                              //     StorageKeys.VISITED_CHARTS
                              //   ) || '{}'
                              // );

                              // if (!userSetDateGroup) {
                              //   visitedCharts[chartId || ''] =
                              //     visitedCharts[chartId || ''] || '';
                              //   LocalStorage.setItem(
                              //     StorageKeys.VISITED_CHARTS,
                              //     JSON.stringify(visitedCharts)
                              //   );
                              // }
                              handleXYSelection(e.target.value, 'x_axis');
                              sendAmplitudeData(CHANGE_X_AXIS, {
                                'Created Using':
                                  currentChartData.created_by_auto_charts
                                    ? 'Auto'
                                    : 'Manual'
                              });
                            }}
                          />
                        </Grid>
                        <Grid
                          item
                          sx={{
                            display: chartType === 'KPI' ? 'none' : 'span'
                          }}
                        >
                          <MDTypography
                            component='span'
                            variant='sm'
                            color='info'
                            mr={1}
                          >
                            split by
                          </MDTypography>
                          <CustomSelect
                            tooltipText={
                              dropDownValues.chart_type === 'PIE'
                                ? 'Split By option is not available for Pie charts. You can change the chart type and then add Split By.'
                                : dropDownValues.segment_field
                                ? 'Change selection of column name. Or select None to remove any selection here.'
                                : 'Select a column to segment the chart'
                            }
                            placeholder={
                              <div style={{ textTransform: 'capitalize' }}>
                                {dropDownValues.chart_type === 'PIE'
                                  ? 'None'
                                  : '- Select -'}
                              </div>
                            }
                            color='white'
                            options={
                              [
                                { label: 'NONE', value: null },
                                ...segmentFields
                              ] || []
                            }
                            value={dropDownValues.segment_field || ''}
                            handleValueChange={(e: any) => {
                              handleDropdownChange(
                                e.target.value,
                                'segment_field'
                              );
                              sendAmplitudeData(CHANGE_SPLIT_BY, {
                                'Stacking Type': visualSettings?.stackChart
                                  ? 'Stacked'
                                  : 'Non Stacked',
                                'Created Using':
                                  currentChartData.created_by_auto_charts
                                    ? 'Auto'
                                    : 'Manual'
                              });
                            }}
                            disabled={dropDownValues.chart_type === 'PIE'}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <MDBox display='flex' alignItems='center' gap={1.25}>
                      <Tooltip
                        title={`Download this ${
                          currentChartData.config.chart_type === 'KPI'
                            ? 'KPI'
                            : 'chart'
                        }`}
                        placement='top'
                      >
                        <IconButton
                          onClick={() => debouncedDownload()}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon='download'
                            typographyProps={{ variant: 'lg' }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          chartType === 'KPI'
                            ? 'Change KPI View settings - add target, show empty values'
                            : `Change chart View settings - grouping & intervals, stacking, order, empty values, etc.`
                        }
                        placement='top'
                      >
                        <IconButton
                          onClick={() => {
                            if (openConfigurator) setConfiguratorFlag(0);
                            else onOpenSlideIn(0, openConfigurator);
                            sendAmplitudeData(CLICKED_SETTINGS);
                          }}
                          disabled={preventClosing}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon={['far', 'cog']}
                            typographyProps={{ variant: 'lg' }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={`Select and change theme from a range of color palettes, set ${
                          chartType === 'KPI' ? 'KPI' : 'chart'
                        } as Gradient etc.`}
                        placement='bottom'
                      >
                        <IconButton
                          onClick={() => {
                            if (openConfigurator) setConfiguratorFlag(1);
                            else onOpenSlideIn(1, openConfigurator);
                            sendAmplitudeData(CLICKED_THEME);
                          }}
                          disabled={preventClosing}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon={['far', 'paintbrush']}
                            typographyProps={{
                              variant: 'lg'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      {/* <Tooltip
                        title={
                          calenderTooltipText?.length
                            ? calenderTooltipText
                            : `Select any column to filter`
                        }
                        placement='bottom'
                      >
                        <IconButton
                          onClick={() => {
                            if (openConfigurator) setConfiguratorFlag(2);
                            else onOpenSlideIn(2, openConfigurator);
                            sendAmplitudeData(CLICKED_FILTER);
                            sendAmplitudeData(CLICKED_DATE_FILTER_ICON);
                          }}
                          disabled={preventClosing}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon={['far', 'calendar-days']}
                            typographyProps={{
                              variant: 'lg'
                            }}
                          />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip
                        title={
                          chartFilters.length > 0
                            ? filterTooltipText
                            : `Select any column to filter`
                        }
                        placement='bottom'
                      >
                        <IconButton
                          onClick={() => {
                            if (openConfigurator) setConfiguratorFlag(2);
                            else onOpenSlideIn(2, openConfigurator);
                            sendAmplitudeData(CLICKED_FILTER);
                          }}
                          disabled={preventClosing}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon={[
                              chartFilters.length > 0 ? 'fas' : 'far',
                              'filter'
                            ]}
                            typographyProps={{
                              variant: 'lg'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={`View/Add ${
                          chartType === 'KPI' ? 'KPI' : 'chart'
                        } comments`}
                        placement='bottom'
                      >
                        <IconButton
                          onClick={() => {
                            if (openConfigurator) setConfiguratorFlag(3);
                            else onOpenSlideIn(3, openConfigurator);
                          }}
                          disabled={preventClosing}
                          sx={{ padding: '0px' }}
                        >
                          <FaIcon
                            icon={['far', 'comment-dots']}
                            typographyProps={{
                              variant: 'lg'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      {chartType !== 'KPI' && (
                        <Tooltip
                          title='DataSquirrel Recommendations'
                          placement='bottom'
                        >
                          <IconButton
                            onClick={() => {
                              if (openConfigurator) setConfiguratorFlag(4);
                              else onOpenSlideIn(4, openConfigurator);
                              sendAmplitudeData(CLICKED_DS_RECOMMENDATION);
                            }}
                            disabled={preventClosing}
                            sx={{ padding: '0px' }}
                          >
                            <FaIcon
                              icon={['far', 'lightbulb-on']}
                              typographyProps={{
                                variant: 'lg'
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={infoText}>
                        <IconButton sx={{ padding: '0px' }}>
                          <FaIcon
                            icon='circle-info'
                            typographyProps={{
                              color: 'primary',
                              variant: 'lg'
                            }}
                            style={{
                              pointerEvents: 'none'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='View in tabular form' placement='bottom'>
                        <IconButton
                          classes={{ root: 'preventBgClose' }}
                          onClick={handleTableIconClick}
                          sx={{
                            display: chartType === 'KPI' ? 'none' : 'span',
                            padding: '0px !important'
                          }}
                        >
                          <FaIcon
                            icon='table'
                            typographyProps={{ color: 'dark', variant: 'lg' }}
                            style={{
                              pointerEvents: 'none'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                </Card>
              )}
              {filterText?.length > 0 && (
                <Grid
                  container
                  my={2}
                  onClick={() => {
                    if (openConfigurator) setConfiguratorFlag(2);
                    else onOpenSlideIn(2, openConfigurator);
                  }}
                >
                  <MDBox
                    sx={{
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      cursor: 'pointer',
                      lineHeight: '5px'
                    }}
                  >
                    <Tooltip
                      title={showTooltip ? filterText : ''}
                      placement='top'
                    >
                      <MDTypography
                        ref={typographyRef}
                        variant='sm'
                        color='info'
                        sx={{
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {filterText}
                      </MDTypography>
                    </Tooltip>
                  </MDBox>
                </Grid>
              )}
            </div>
            <Grid
              item
              flex={1}
              sx={{
                width: '100%',
                paddingX: '0px !important',
                marginBottom: '2px'
              }}
            >
              <MDBox position='relative'>
                <Card
                  sx={{
                    padding: '0',
                    height: '100%',
                    transition: 'width 0.5s',
                    width: openConfigurator ? 'calc(100% - 375px)' : '100%'
                  }}
                  ref={cardRef}
                >
                  <div
                    style={{
                      position: 'relative',
                      height: `calc(100vh - ${
                        chartType === 'KPI'
                          ? chartHeaderHeight
                            ? chartHeaderHeight + 80
                            : 269
                          : chartHeaderHeight
                          ? chartHeaderHeight + 130
                          : 319
                      }px)`,
                      overflow: chartType === 'Bar' ? 'auto' : 'hidden'
                    }}
                  >
                    {chartType !== 'KPI' && (
                      <div
                        style={{
                          height: '150px',
                          width: '100%',
                          position: 'absolute',
                          top: 5,
                          background: 'white'
                        }}
                      />
                    )}
                    <MDBox
                      ref={domRef}
                      sx={{
                        backgroundColor: '#ffffff',
                        padding: '10px',
                        height: '100%',
                        display: chartType !== 'KPI' ? 'flex' : undefined,
                        justifyContent:
                          chartType !== 'KPI' ? 'center' : undefined
                      }}
                      id='editChartContainer'
                    >
                      {debouncedChart}
                    </MDBox>
                  </div>
                </Card>
                {openConfigurator && (
                  <Configurator
                    width='365px'
                    configuratorFlag={configuratorFlag}
                    setConfiguratorFlag={setConfiguratorFlag}
                    onCloseConfigurator={() => onCloseConfigurator()}
                    isEditChart
                    isLoading={isChartLoading}
                    hideRecommendations={chartType === 'KPI' ? true : false}
                    isProjectBoard={true}
                    paddingBottom={'0px'}
                  >
                    <MDBox
                      id='configurator-test'
                      height={
                        chartHeaderHeight && chartType !== 'KPI'
                          ? `calc(100vh - ${chartHeaderHeight + 216}px)`
                          : chartHeaderHeight && chartType === 'KPI'
                          ? `calc(100vh - ${chartHeaderHeight + 162}px)`
                          : '70vh'
                      }
                    >
                      <If condition={configuratorFlag === 0}>
                        <Settings
                          data={chartData}
                          config={config}
                          filters={visualSettings}
                          setFilters={setVisualSettings}
                          isPie={dropDownValues.chart_type === 'PIE'}
                          isBar={dropDownValues.chart_type === 'Bar'}
                          hasSegments={!!dropDownValues.segment_field}
                          height={cardRef?.current?.offsetHeight}
                          isChartLoading={isChartLoading}
                          isDate={xAxis?.type === 'DATE'}
                          isDateTime={xAxis?.type === 'DATETIME'}
                          isNumberSelf={
                            numberEquivalents.includes(xAxis?.type) &&
                            numberEquivalents.includes(yAxis?.type) &&
                            xAxis?.value === yAxis?.value
                          }
                          dateGroup={
                            userSetDateGroup === null
                              ? currentChartData?.config.date_group === 'none'
                                ? ''
                                : currentChartData?.config.date_group
                              : userSetDateGroup
                          }
                          setDateGroup={handleDateGroupChange}
                          setIsManuallySetStackChart={
                            setIsManuallySetStackChart
                          }
                          setIsManuallySetLegends={setIsManuallySetLegends}
                          stackColumnName={stackColumnName}
                          isEditChart
                          isKpiChart={chartType === 'KPI' ? true : false}
                          xAxisType={xAxis?.type}
                          availableSettings={availableSettings}
                        />
                      </If>
                      <If condition={configuratorFlag === 1}>
                        <MDBox
                          height={`${
                            (cardRef?.current?.offsetHeight || 95) - 95
                          }px`}
                        >
                          <ThemeMenu
                            filters={visualSettings}
                            setFilters={setVisualSettings}
                            selectedTheme={selectedTheme || null}
                            setSelectedTheme={(colors: string[] | null) => {
                              if (chartId) {
                                setVisualSettings((prev) => ({
                                  ...prev,
                                  theme_connection_status: {
                                    ...(prev?.theme_connection_status || {
                                      ['3d_charts']: true,
                                      gradient_charts: true,
                                      multicolor_charts: true
                                    }),
                                    colorThemeToggle: colors
                                      ? projectColorSet?.join(',') ===
                                        colors?.join(',')
                                      : true
                                  }
                                }));
                                updateTheme({
                                  id: chartId as string,
                                  payload: {
                                    colorset: colors ? colors?.join(',') : null
                                  }
                                });
                              }
                            }}
                            chartType={chartType as string}
                            hasSegments={!!dropDownValues.segment_field}
                          />
                        </MDBox>
                      </If>
                      <If condition={configuratorFlag === 2}>
                        <GlobalFilter
                          chartFilters={
                            (currentChartData.config
                              .filters as IGlobalFilters) || {}
                          }
                          dynamicFilters={
                            (currentChartData.config
                              .dynamic_filters as IGlobalFilters) || {}
                          }
                          projectId={currentChartData.chart_project}
                          projectBoardFilters={
                            projectData?.filters as IGlobalFilters
                          }
                          projectBoardDynamicFilters={
                            projectData?.dynamic_filters || {}
                          }
                          visualSettings={visualSettings}
                          setVisualSettings={setVisualSettings}
                          chartRefetchFunction={chartRefetchFunction}
                          xAxisLabel={xAxis?.label}
                          iconTooltipText={filterTooltipText}
                          dateGroup={activeDateGroup}
                          setRefetchChart={setRefetchChart}
                          setIsManuallySetDashboardFilterToggle={
                            setIsManuallySetDashboardFilterToggle
                          }
                        />
                        <Filter
                          data={chartData}
                          visualSettings={visualSettings}
                          setVisualSettings={setVisualSettings}
                          isDate={xAxis?.type === 'DATE'}
                          isDateTime={xAxis?.type === 'DATETIME'}
                          disableFilter={
                            chartType === 'KPI' &&
                            dropDownValues.chart_operation !== 'COUNT' &&
                            dropDownValues.chart_operation !== 'UNIQUECOUNT' &&
                            dropDownValues.chart_operation !== 'SUM'
                          }
                          isXaxisNumber={numberEquivalents.includes(
                            xAxis?.type
                          )}
                          height={cardRef?.current?.offsetHeight}
                          {...filterProps}
                        />
                      </If>
                      <If condition={configuratorFlag === 3}>
                        <CommentsList
                          commentsList={commentsList}
                          onPostComment={onCreateChartComment}
                          height={cardRef?.current?.offsetHeight}
                          isEditChartOrPB
                        />
                      </If>
                      <If condition={configuratorFlag === 4}>
                        <MDBox
                          sx={{
                            display: 'block',
                            alignItems: 'center'
                          }}
                          height={`${
                            (cardRef?.current?.offsetHeight || 100) - 100
                          }px`}
                        >
                          <Switch
                            sx={{
                              width: '2.4rem',
                              height: '1.15rem',
                              '& .MuiSwitch-thumb': {
                                width: '.9rem',
                                height: '.9rem'
                              }
                            }}
                            checked={isAutoCategorizationOpen}
                            onChange={() => handleToggleAutoCategorization()}
                            disabled={
                              segmentField
                                ? (segmentField?.type !== 'TEXT' ||
                                    segmentField?.contains_sensitive) &&
                                  (xAxis?.type !== 'TEXT' ||
                                    xAxis?.contains_sensitive)
                                : xAxis?.type !== 'TEXT' ||
                                  xAxis?.contains_sensitive
                            }
                          />
                          {subscriptionFlag &&
                            (isAutoCategorizationEnabledByPlan ? (
                              <FaIcon
                                icon='lock-open'
                                style={{
                                  marginLeft: '7px',
                                  marginBottom: '1px'
                                }}
                                typographyProps={{
                                  variant: 'sm',
                                  color: 'primary'
                                }}
                              />
                            ) : (
                              <FaIcon
                                icon='lock'
                                style={{
                                  marginLeft: '7px',
                                  marginBottom: '1px'
                                }}
                                typographyProps={{
                                  variant: 'sm',
                                  color: 'primary'
                                }}
                              />
                            ))}
                          <MDTypography
                            variant='sm'
                            fontWeight='regular'
                            mt={0.2}
                            color='info'
                            ml={1}
                          >
                            Auto categorization
                          </MDTypography>
                        </MDBox>
                      </If>
                    </MDBox>
                  </Configurator>
                )}
              </MDBox>
            </Grid>
          </Grid>
        )}

        {!!invalidOperation && (
          <Snackbar
            open={!!invalidOperation}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
          >
            <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
              <Alert
                severity='error'
                sx={{ marginTop: infoSnackBarOpen ? '70px' : '10px' }}
                onClose={handleCloseSnackbar}
              >
                {invalidOperation} is changed to COUNT because of the change of
                column name.
              </Alert>
            </Grid>
          </Snackbar>
        )}
        {axisFlipped && (
          <Snackbar
            open={axisFlipped}
            autoHideDuration={6000}
            onClose={handleCloseAxisFlippedSnackbar}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center'
            }}
          >
            <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
              <Alert
                severity='info'
                sx={{ marginTop: invalidOperation?.length ? '70px' : '10px' }}
                onClose={handleCloseAxisFlippedSnackbar}
              >
                We have flipped the columns of this chart for improved data
                representation.
              </Alert>
            </Grid>
          </Snackbar>
        )}
        {chartData && config && showDownloadPreview && (
          <DownloadPreviewModal
            open={showDownloadPreview}
            chartData={chartData}
            chartConfig={config}
            chartConfigData={chartConfigData}
            chartType={
              dropDownValues.chart_type || chartConfigData?.config?.chart_type
            }
            visualSettings={visualSettings}
            chartTitle={currentChartData?.chart_title || ''}
            chartDescription={currentChartData?.chart_short_desc || ''}
            projectName={currentChartData?.chart_project_name}
            chartOperation={currentChartData?.config.chart_operation}
            chartId={chartId as string}
            onClose={() => setShowDownloadPreview(false)}
            dateGroup={activeDateGroup}
            colorSet={
              getChartTheme(
                currentChartData as unknown as ChartConfigData
              ) as any
            }
            projectId=''
            isSegmented={
              !!(
                dropDownValues.segment_field ||
                chartConfigData?.config?.segment_field
              )
            }
            isDate={xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME'}
          />
        )}
        <Snackbar
          open={infoSnackBarOpen}
          autoHideDuration={6000}
          onClose={() => setInfoSnackBarOpen(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
        >
          <Grid container sx={{ flex: 1, justifyContent: 'center' }}>
            <Alert
              severity='info'
              sx={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}
              onClose={() => setInfoSnackBarOpen(false)}
            >
              {snackBarMessage}
            </Alert>
          </Grid>
        </Snackbar>
        {isAutoCategorizationOpen && (
          <AutoCategorizationModal
            chartId={chartId as string}
            chartData={chartData}
            columnOptions={getAutoCategorizationColumnOptions()}
            isAutoCategorizationOpen={isAutoCategorizationOpen}
            isAutoCategorizationLoading={isAutoCategorizationLoading}
            segmentField={segmentField?.label}
            onClose={() => {
              setAutoCategorizationOpen(false);
              setAutoCategorizationLoading(false);
            }}
            onInitiateAutoCategorization={(field, count) =>
              handleInitiateAutoCategorization(field, count)
            }
            onCompleteCategorization={(
              columnId: string,
              selectedColumn: string
            ) => handleCompleteAutoCategorization(columnId, selectedColumn)}
            isSelfChart={xAxis?.value === yAxis?.value ? 'yes' : 'no'}
          />
        )}
        {openSqBChatWidget && (
          <SqBChatWidget
            open={openSqBChatWidget}
            onClose={() => setOpenSqBChatWidget(false)}
            projectId={projectData?.id || ''}
            chartId={chartId}
            setRefetchChartConfigs={setRefetchChart}
          />
        )}

        <Tooltip title=''>
          <MDBox
            sx={({ palette }) => ({
              background: linearGradient(
                palette.gradients.secondary.main,
                palette.gradients.secondary.state
              ),
              position: 'fixed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bottom: 65,
              right: 15,
              borderRadius: 50,
              boxShadow: '0 3px 10px rgb(0 0 0 / 0.2)',
              cursor: 'pointer',
              zIndex: 100,
              padding: '5px'
            })}
            onClick={() => {
              setOpenSqBChatWidget(true);
            }}
          >
            <MDBox
              component='img'
              src={brandLogo}
              alt='Brand'
              sx={{
                height: 55,
                width: 55,
                padding: '11px'
              }}
            />
          </MDBox>
        </Tooltip>
        {openCleanActionsModal && (
          <CleanActionsModal
            open={openCleanActionsModal}
            onClose={() => setOpenCleanActionsModal(false)}
            setDownloadClicked={handleCleanedDataDownloadOrCancel}
          />
        )}
        {openDownloadModal && (
          <ReportGenerationModal
            open={openDownloadModal}
            onClose={onClickDownloadOrCancel}
            disableDownloadReport
            themeData={[]}
            chartConfigs={[]}
            svgGeneratedCharts={[]}
            cleanedColumnsRedirect={cleanedColumnsRedirect}
          />
        )}
        {openChartInsightsModal && (
          <InsightsModal
            open={openChartInsightsModal}
            onClose={() => setOpenChartInsightsModal(false)}
            insights={'[]'}
            currentDesc={currentChartData?.chart_short_desc}
            chartId={currentChartData?.id}
            isChartInsights
          />
        )}
      </MDBox>

      {dropdownData && chartData && chartType !== 'KPI' && (
        <Card
          sx={{
            mt: 1,
            mb: 0.5,
            width: 'calc(100% - 8px)',
            display: !openHeatMap ? 'flex' : null,
            alignItems: !openHeatMap ? 'center' : null
          }}
        >
          {!openHeatMap && (
            <Tooltip title={!openHeatMap ? 'Show chart in tabular form' : ''}>
              <MDBox
                onClick={handleHeatMapArrowClick}
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={'max-content'}
                sx={{
                  cursor: 'pointer'
                }}
              >
                <MDTypography
                  variant='sm'
                  fontWeight='bold'
                  color='secondary'
                  textGradient
                >
                  Show Data
                </MDTypography>
                <IconButton sx={{ ml: '10px' }}>
                  <FaIcon
                    icon='arrow-down'
                    typographyProps={{ color: 'secondary', variant: 'lg' }}
                  />
                </IconButton>
              </MDBox>
            </Tooltip>
          )}
          {openHeatMap &&
            (isChartLoading || reloadDataTable ? (
              <Card
                sx={{
                  height: 250,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <CircularProgress color='secondary' ref={heatMapRef} />
              </Card>
            ) : (
              <MDBox id='heat-map'>
                <HeatMap
                  xAxisKey={xAxis?.label}
                  dropDownValues={dropDownValues}
                  segmentFields={segmentFields}
                  tableData={chartData}
                  xAxisLabelsExcluded={
                    visualSettings?.filters?.xAxisLabelsExcluded
                  }
                  isFilterActive={
                    visualSettings?.filters?.type === 'bottom' ||
                    visualSettings?.filters?.type === 'top'
                  }
                  isDate={xAxis?.type === 'DATE' || xAxis?.type === 'DATETIME'}
                  isDateTime={xAxis?.type === 'DATETIME'}
                  ref={heatMapRef}
                  openHeatMap={openHeatMap}
                  availableFields={dropdownData?.available_fields}
                  handleHeatMapArrowClick={handleHeatMapArrowClick}
                  watermarkProps={{
                    isKPI: chartType === 'KPI',
                    onClickRemoveWaterMark: onClickRemoveWaterMark
                  }}
                  showAsPercentage={visualSettings?.showAsPercentage as boolean}
                />
              </MDBox>
            ))}
        </Card>
      )}
      {currentChartData?.chart_long_desc && chartData && (
        <MDTypography
          width='fit-content'
          variant='h5'
          color='info'
          sx={{
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-wrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            paddingX: '5px',
            my: 1,
            mr: 10
          }}
        >
          {currentChartData?.chart_long_desc}
        </MDTypography>
      )}
    </MainLayout>
  );
};

export default EditChartComponent;
